import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { StorageUsage } from 'app/services/dashboard/dashboard.service';

@Component({
    selector: 'app-storage-detail',
    templateUrl: './storage-detail.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatIconModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StorageDetailComponent {

    readonly storage: StorageUsage;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: StorageUsage,
        private _dialogRef: MatDialogRef<StorageDetailComponent>
    )
    {
        // O modal recebe direto o objeto interno StorageUsage (quem abre
        // passa this.storageUsage / response.data). Fallback vazio apenas
        // por robustez contra dados ausentes.
        this.storage = data ?? ({} as StorageUsage);
    }

    /**
     * Formata bytes para exibição legível ("2.5 GB", "635.4 MB", "0 B").
     */
    formatBytes(bytes: number): string
    {
        if (!bytes || bytes <= 0)
        {
            return '0 B';
        }

        const units: string[] = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
        const value = bytes / Math.pow(1024, i);

        return `${value.toFixed(1)} ${units[i]}`;
    }

    /**
     * Indica se o snapshot ainda não foi computado pela fila
     * (computed_at null/vazio) — bucket_bytes pode estar zerado
     * temporariamente.
     */
    get storageComputing(): boolean
    {
        return !!this.storage && !this.storage.computed_at;
    }

    /**
     * Percentual de uso real (0-100), com fallback seguro para payload ausente.
     */
    get usedPercent(): number
    {
        const percent = Number(this.storage?.used_percent) || 0;
        return Math.min(Math.max(percent, 0), 100);
    }

    /**
     * Percentual que um tamanho representa em relação à cota total (0-100).
     */
    percentOfTotal(bytes: number): number
    {
        const total = Number(this.storage?.total_bytes) || 0;
        if (total <= 0)
        {
            return 0;
        }

        return Math.min(Math.max((Number(bytes) || 0) / total * 100, 0), 100);
    }

    /**
     * Contador com fallback seguro (payload ausente → 0).
     */
    counter(key: keyof StorageUsage['counters']): number
    {
        return Number(this.storage?.counters?.[key]) || 0;
    }

    /**
     * Fecha o modal.
     */
    close(): void
    {
        this._dialogRef.close();
    }
}